// Card de resultado para compartilhar (1080×1350, bom para Twitter/X, Instagram e
// WhatsApp): colocação, treinador, time final com estrelas, sinergias e números.
import { computeActiveTraits, type SetDef, type UnitInstance } from "@pokechess/shared";
import { set, speciesFor, speciesName, traitName } from "./data";
import { t } from "./i18n";
import { atlasFrame, loadSprites } from "./sprites";

export interface ShareInfo {
  nickname: string;
  title?: string | null;
  avatar: number | null;
  placement: number;
  players: number;
  level: number;
  stage: number;
  round: number;
  board: UnitInstance[];
  shiny: number[];
  pvpWins?: number;
  pokedollars?: number;
}

const W = 1080, H = 1350;
const MEDAL = ["#f2c230", "#c9d3df", "#d98a4a"];
const FONT = '"Jersey 10", "DotGothic16", system-ui, sans-serif';

/** Cor de cada tipo/classe, lida do tema (a mesma dos selos do jogo). */
function traitColor(key: string): string {
  const el = document.createElement("span");
  el.className = "bw-type";
  el.dataset.type = key;
  el.style.position = "absolute";
  el.style.visibility = "hidden";
  document.body.appendChild(el);
  const c = getComputedStyle(el).backgroundColor;
  el.remove();
  return c && c !== "rgba(0, 0, 0, 0)" ? c : "#5b6b84";
}

const images = new Map<string, Promise<HTMLImageElement>>();
function img(url: string): Promise<HTMLImageElement> {
  if (!images.has(url)) {
    images.set(url, new Promise((resolve, reject) => {
      const i = new Image();
      i.onload = () => resolve(i);
      i.onerror = reject;
      i.src = url;
    }));
  }
  return images.get(url)!;
}

async function drawSprite(g: CanvasRenderingContext2D, species: number, shiny: boolean, cx: number, bottom: number, size: number) {
  const f = atlasFrame(species, shiny);
  if (!f) return;
  const sheet = await img(f.url);
  const k = size / Math.max(f.w, f.h);
  g.drawImage(sheet, f.x, f.y, f.w, f.h, Math.round(cx - (f.w * k) / 2), Math.round(bottom - f.h * k), Math.round(f.w * k), Math.round(f.h * k));
}

function roundRect(g: CanvasRenderingContext2D, x: number, y: number, w: number, h: number, r: number) {
  g.beginPath();
  g.moveTo(x + r, y);
  g.arcTo(x + w, y, x + w, y + h, r);
  g.arcTo(x + w, y + h, x, y + h, r);
  g.arcTo(x, y + h, x, y, r);
  g.arcTo(x, y, x + w, y, r);
  g.closePath();
}

function text(g: CanvasRenderingContext2D, s: string, x: number, y: number, size: number, color = "#fff", align: CanvasTextAlign = "left", outline = 0) {
  g.font = `${size}px ${FONT}`;
  g.textAlign = align;
  g.textBaseline = "alphabetic";
  if (outline) {
    g.lineWidth = outline;
    g.strokeStyle = "#1d2330";
    g.lineJoin = "round";
    g.strokeText(s, x, y);
  }
  g.fillStyle = color;
  g.fillText(s, x, y);
}

function star(g: CanvasRenderingContext2D, cx: number, cy: number, r: number, fill: string) {
  g.beginPath();
  for (let i = 0; i < 10; i++) {
    const a = -Math.PI / 2 + (i * Math.PI) / 5;
    const rr = i % 2 ? r * 0.45 : r;
    g.lineTo(cx + Math.cos(a) * rr, cy + Math.sin(a) * rr);
  }
  g.closePath();
  g.fillStyle = fill;
  g.fill();
  g.lineWidth = 3;
  g.strokeStyle = "#1d2330";
  g.stroke();
}

export async function renderShareCard(info: ShareInfo): Promise<Blob> {
  await Promise.all([loadSprites(), document.fonts.load(`80px ${FONT}`).catch(() => undefined)]);
  const c = document.createElement("canvas");
  c.width = W;
  c.height = H;
  const g = c.getContext("2d")!;
  g.imageSmoothingEnabled = false;

  // fundo: degradê + hexágonos
  const bg = g.createLinearGradient(0, 0, 0, H);
  bg.addColorStop(0, "#1d2a4a");
  bg.addColorStop(1, "#2a6fdb");
  g.fillStyle = bg;
  g.fillRect(0, 0, W, H);
  g.strokeStyle = "rgba(255,255,255,0.06)";
  g.lineWidth = 3;
  for (let y = -40; y < H + 80; y += 78) {
    for (let x = -40; x < W + 80; x += 90) {
      const ox = (Math.round(y / 78) % 2) * 45;
      g.beginPath();
      for (let i = 0; i < 6; i++) g.lineTo(x + ox + 38 * Math.cos((Math.PI / 3) * i + Math.PI / 6), y + 38 * Math.sin((Math.PI / 3) * i + Math.PI / 6));
      g.closePath();
      g.stroke();
    }
  }

  // topo: logo
  const logo = await img("/icons/logo-256.png").catch(() => null);
  const lx = logo ? 196 : 60;
  if (logo) {
    g.save();
    g.beginPath();
    g.arc(60 + 62, 112, 62, 0, Math.PI * 2);
    g.clip();
    g.drawImage(logo, 60, 50, 124, 124);
    g.restore();
    g.lineWidth = 6;
    g.strokeStyle = "#10131a";
    g.beginPath();
    g.arc(60 + 62, 112, 62, 0, Math.PI * 2);
    g.stroke();
  }
  text(g, "PokéChess", lx, 120, 100, "#fff", "left", 14);
  text(g, t("share.tagline"), lx + 4, 168, 36, "#cfe0ff");

  // colocação
  const medal = MEDAL[info.placement - 1] ?? "#8aa0c0";
  g.beginPath();
  g.arc(W - 170, 140, 110, 0, Math.PI * 2);
  g.fillStyle = medal;
  g.fill();
  g.lineWidth = 10;
  g.strokeStyle = "#1d2330";
  g.stroke();
  text(g, `${info.placement}º`, W - 170, 180, 120, "#1d2330", "center");
  text(g, info.players > 1 ? t("share.ofN", { n: info.players }) : t("share.solo"), W - 170, 300, 40, "#fff", "center", 8);

  // treinador
  roundRect(g, 50, 230, 640, 150, 26);
  g.fillStyle = "rgba(255,255,255,0.95)";
  g.fill();
  g.lineWidth = 8;
  g.strokeStyle = "#1d2330";
  g.stroke();
  if (info.avatar) await drawSprite(g, info.avatar, false, 140, 360, 120);
  text(g, info.nickname, 220, 302, 64, "#1d2330");
  text(g, info.title ?? t("share.trainer"), 222, 352, 36, "#5b6b84");

  // time final
  roundRect(g, 50, 420, W - 100, 560, 30);
  g.fillStyle = "rgba(10,16,32,0.55)";
  g.fill();
  g.lineWidth = 6;
  g.strokeStyle = "rgba(255,255,255,0.25)";
  g.stroke();
  text(g, t("share.team"), 90, 488, 48, "#fff");
  const units = [...info.board].sort((a, b) => b.star - a.star || (set.units.find((u) => u.id === b.unitId)?.cost ?? 0) - (set.units.find((u) => u.id === a.unitId)?.cost ?? 0)).slice(0, 10);
  const cols = 5, cellW = (W - 140) / cols, cellH = 230;
  for (const [i, u] of units.entries()) {
    const cx = 70 + cellW * (i % cols) + cellW / 2;
    const top = 510 + Math.floor(i / cols) * cellH;
    const species = speciesFor(u.unitId, u.star, u.branch);
    roundRect(g, cx - cellW / 2 + 8, top, cellW - 16, cellH - 16, 18);
    g.fillStyle = u.star === 3 ? "rgba(242,194,48,0.30)" : u.star === 2 ? "rgba(200,215,235,0.22)" : "rgba(255,255,255,0.10)";
    g.fill();
    await drawSprite(g, species, info.shiny.includes(species), cx, top + 150, 130);
    const starFill = u.star === 3 ? "#f2c230" : u.star === 2 ? "#e6edf6" : "#b9824f";
    for (let s = 0; s < u.star; s++) star(g, cx + (s - (u.star - 1) / 2) * 34, top + 178, 15, starFill);
    text(g, speciesName(species) ?? u.unitId, cx, top + 208, 28, "#fff", "center", 6);
  }
  if (!units.length) text(g, "—", W / 2, 760, 80, "#fff", "center");

  // sinergias ativas
  const traits = computeActiveTraits(info.board, set as SetDef).filter((a) => a.tier >= 0).slice(0, 6);
  let x = 60;
  for (const a of traits) {
    const label = `${a.count} ${traitName(a.key)}`;
    g.font = `40px ${FONT}`;
    const w = g.measureText(label).width + 44;
    if (x + w > W - 60) break;
    roundRect(g, x, 1010, w, 64, 16);
    g.fillStyle = traitColor(a.key);
    g.fill();
    g.lineWidth = 5;
    g.strokeStyle = "#1d2330";
    g.stroke();
    text(g, label, x + w / 2, 1056, 40, "#fff", "center", 6);
    x += w + 14;
  }

  // números
  const stats = [
    t("share.level", { n: info.level }),
    t("share.round", { r: `${info.stage}-${info.round}` }),
    ...(info.pvpWins !== undefined ? [t("share.wins", { n: info.pvpWins })] : []),
    ...(info.pokedollars ? [`+₽ ${info.pokedollars}`] : []),
  ];
  text(g, stats.join("  ·  "), W / 2, 1150, 46, "#fff", "center", 8);

  // rodapé
  g.fillStyle = "#1d2330";
  g.fillRect(0, H - 130, W, 130);
  text(g, t("share.cta"), W / 2, H - 76, 50, "#fff", "center");
  text(g, location.host, W / 2, H - 30, 36, "#9fc2ff", "center");

  return new Promise((resolve, reject) => c.toBlob((b) => (b ? resolve(b) : reject(new Error("canvas"))), "image/png"));
}

/** Compartilha (celular) ou baixa o PNG (computador). */
export async function shareResult(info: ShareInfo): Promise<"shared" | "downloaded"> {
  const blob = await renderShareCard(info);
  const file = new File([blob], `pokechess-${info.placement}lugar.png`, { type: "image/png" });
  const textMsg = t("share.text", { place: info.placement, n: info.players, url: location.origin });
  const nav = navigator as Navigator & { canShare?: (d: ShareData) => boolean };
  if (nav.canShare?.({ files: [file] })) {
    try {
      await navigator.share({ files: [file], text: textMsg });
      return "shared";
    } catch (e) {
      if ((e as Error).name === "AbortError") return "shared";
    }
  }
  const a = document.createElement("a");
  a.href = URL.createObjectURL(blob);
  a.download = file.name;
  a.click();
  setTimeout(() => URL.revokeObjectURL(a.href), 5000);
  return "downloaded";
}
